// src/theme/components/button.js
import { defineStyle, defineStyleConfig } from "@chakra-ui/react";

// Faz 1 fix · _focusVisible boxShadow icin semantic focusRing token'lari kullanilir.
// Eski `var(--chakra-colors-${c}-500-alpha-40)` deseni sadece 'brand' ve 'red' icin
// resolve oluyordu (green/purple/gray/amber/yellow'da broken). focusRing.default brand
// tonlu, focusRing.error red tonludur · diger colorScheme'ler icin default'a duser.
const focusRingForScheme = (c) => {
  if (c === 'red' || c === 'rose') return 'focusRing.error';
  if (c === 'green' || c === 'emerald') return 'focusRing.success';
  if (c === 'yellow' || c === 'amber') return 'focusRing.warning';
  return 'focusRing.default';
};

// AI Temalı Solid Buton Varyantı
const aiSolid = defineStyle((props) => {
  const { colorScheme: c } = props; // 'brand' veya diğerleri (red, green vb.)
  let bg = `${c}.500`;
  let color = "white";
  let hoverBg = `${c}.600`;
  let activeBg = `${c}.700`;

  if (props.colorMode === "dark") {
    bg = `${c}.300`;
    color = "gray.900"; // Koyu renkli arka plan üzerinde daha iyi kontrast
    hoverBg = `${c}.400`;
    activeBg = `${c}.200`;
    if (c === 'gray') { // Gri butonlar için özel renkler
        bg = 'whiteAlpha.200';
        color = 'whiteAlpha.800';
        hoverBg = 'whiteAlpha.300';
        activeBg = 'whiteAlpha.400';
    }
  }

  // 'brand' colorScheme için özel AI glow efekti
  let hoverBoxShadow = "md";
  if (c === "brand") {
    hoverBoxShadow = props.colorMode === "dark" ? "var(--chakra-shadows-aiGlowAccentMd)" : "var(--chakra-shadows-aiGlowAccentSm)";
  }

  // Faz 2 a11y · WCAG kontrast fix: brand.500 (#4DA8FF) uzerine beyaz metin 2.51:1 = FAIL AA.
  // Light mode'da brand.800+ tonlarini kullan (>=5.0:1 normal text AA pass). Dark mode'da
  // bg zaten brand.300 (acik) + gray.900 (koyu) text · yuksek kontrast, dokunmaya gerek yok.
  // Diger colorScheme'leri (red/green/...) etkilemez, sadece brand override.
  const brandBgLight = "brand.800";
  const brandHoverLight = "brand.900";
  const brandActiveLight = "brand.900";
  const isLight = props.colorMode !== "dark";

  return {
    bg: c === "brand" ? (isLight ? brandBgLight : "accent") : bg,
    color: color,
    borderRadius: "lg", // Daha modern: 12px
    fontWeight: "semibold",
    transitionProperty: "common",
    transitionDuration: "var(--motion-duration-base)",
    transitionTimingFunction: "var(--motion-ease-apple)",
    _hover: {
      bg: c === "brand" ? (isLight ? brandHoverLight : "accentHover") : hoverBg,
      transform: "translateY(-2px)",
      boxShadow: hoverBoxShadow,
    },
    _active: {
      bg: c === "brand" ? (isLight ? brandActiveLight : "accentActive") : activeBg,
      transform: "translateY(-1px)",
      boxShadow: "sm",
    },
    _disabled: {
      bg: props.colorMode === 'dark' ? "whiteAlpha.100" : "gray.300",
      boxShadow: "none",
      transform: "none",
      opacity: 0.6,
      cursor: "not-allowed",
    },
    _focusVisible: { // Klavye ile odaklandığında
      boxShadow: focusRingForScheme(c),
      outline: "none",
    },
  };
});

// Auth sayfaları için merkezi primary CTA varyantı.
// LoginPage / RegisterPage / Reset / RequestReset hepsi bunu kullanır.
// Tek noktadan: yükseklik, radius, glow, geçişler, focus halkası.
// Faz 2 a11y · brand.500 yerine brand.800+ (light mode) · accent token dark mode'da
// bg=brand.400 + text=gray.900 olarak zaten yuksek kontrast.
const authPrimary = defineStyle((props) => ({
  bg: props.colorMode === 'dark' ? 'accent' : 'brand.800',
  color: 'textOnAccent',
  borderRadius: 'xl',
  fontWeight: 'semibold',
  fontSize: 'md',
  letterSpacing: 'normal',
  textTransform: 'none',
  width: 'full',
  h: { base: '52px', md: '56px' },
  transitionProperty: 'background-color, transform, box-shadow',
  transitionDuration: 'var(--motion-duration-base)',
  transitionTimingFunction: 'var(--motion-ease-apple)',
  _hover: {
    bg: props.colorMode === 'dark' ? 'accentHover' : 'brand.900',
    transform: 'translateY(-2px)',
    boxShadow: 'aiGlowAccentMd',
    _disabled: {
      transform: 'none',
      boxShadow: 'none',
      bg: props.colorMode === 'dark' ? 'accent' : 'brand.800',
    },
  },
  _active: {
    bg: props.colorMode === 'dark' ? 'accentActive' : 'brand.900',
    transform: 'translateY(0)',
    boxShadow: 'sm',
  },
  _focusVisible: {
    boxShadow: 'focusOutlineAccent',
    outline: 'none',
  },
  _disabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
    transform: 'none',
    boxShadow: 'none',
  },
}));

// Auth sayfaları için secondary outline (Google ile giriş gibi).
const authOutline = defineStyle(() => ({
  bg: 'transparent',
  color: 'textPrimary',
  borderWidth: '1px',
  borderColor: 'borderPrimary',
  borderRadius: 'xl',
  fontWeight: 'semibold',
  fontSize: 'sm',
  width: 'full',
  h: { base: '46px', md: '48px' },
  transitionProperty: 'background-color, border-color, transform',
  transitionDuration: 'var(--motion-duration-fast)',
  transitionTimingFunction: 'var(--motion-ease-apple)',
  _hover: {
    bg: 'bgSecondary',
    borderColor: 'accent',
  },
  _active: {
    bg: 'bgTertiary',
  },
  _focusVisible: {
    boxShadow: 'focusOutlineAccent',
    outline: 'none',
  },
  _disabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
}));

// Glassmorphism Buton Varyantı
const glass = defineStyle(() => ({
  bg: "bgPrimary",
  color: "textPrimary",
  borderRadius: "lg",
  borderWidth: "1px",
  borderColor: "borderPrimary",
  backdropFilter: "saturate(180%) blur(18px)",
  WebkitBackdropFilter: "saturate(180%) blur(18px)",
  boxShadow: "sm",
  _hover: {
    bg: "bgSecondary",
    borderColor: "borderAccent",
    boxShadow: "var(--chakra-shadows-aiGlowAccentSm)",
    transform: "translateY(-2px)",
  },
  _active: {
    bg: "bgTertiary",
    transform: "translateY(0)",
    boxShadow: "sm",
  },
  _disabled: {
    opacity: 0.6,
    cursor: "not-allowed",
    transform: "none",
    boxShadow: "none",
  },
  _focusVisible: {
    boxShadow: "var(--chakra-shadows-focusOutlineAccent)",
    outline: "none",
  },
}));

// AI Temalı Outline Buton Varyantı
const aiOutline = defineStyle((props) => {
  const { colorScheme: c } = props;
  let borderColor = `${c}.500`;
  let color = `${c}.500`;
  let hoverBg = `${c}.50`; // Hafif bir arka plan
  let activeBg = `${c}.100`;

  if (props.colorMode === "dark") {
    borderColor = `${c}.300`;
    color = `${c}.300`;
    hoverBg = `var(--chakra-colors-${c}-300-alpha-30)`; // colors.js'de tanımlı alpha'lı renk
    activeBg = `var(--chakra-colors-${c}-300-alpha-50)`;
     if (c === 'gray') {
        borderColor = 'whiteAlpha.400';
        color = 'whiteAlpha.700';
        hoverBg = 'whiteAlpha.100';
        activeBg = 'whiteAlpha.200';
    }
  }

  return {
    borderWidth: "2px",
    borderColor: c === "brand" ? "accent" : borderColor,
    color: c === "brand" ? "accent" : color,
    bg: "transparent",
    borderRadius: "lg",
    fontWeight: "semibold",
    transitionProperty: "common",
    transitionDuration: "var(--motion-duration-base)",
    transitionTimingFunction: "var(--motion-ease-apple)",
    _hover: {
      bg: hoverBg,
      borderColor: c === "brand" ? "accentHover" : `${c}.600`,
      color: c === "brand" ? "accentHover" : `${c}.600`,
      transform: "translateY(-1px)",
      boxShadow: c === "brand" ? "var(--chakra-shadows-aiGlowAccentSm)" : "xs",
    },
    _active: {
      bg: activeBg,
      borderColor: c === "brand" ? "accentActive" : `${c}.700`,
      color: c === "brand" ? "accentActive" : `${c}.700`,
    },
    _disabled: {
      borderColor: props.colorMode === 'dark' ? "whiteAlpha.200" : "gray.300",
      color: props.colorMode === 'dark' ? "whiteAlpha.300" : "gray.400",
      bg: "transparent",
      opacity: 0.5,
      cursor: "not-allowed",
      transform: "none",
      boxShadow: "none",
    },
    _focusVisible: {
      // Faz 1 fix · semantic focusRing token (sadece brand/red icin resolve eden
      // `var(--chakra-colors-${c}-500-alpha-40)` yerine).
      boxShadow: focusRingForScheme(c),
      outline: "none",
    },
  };
});

// Quiz Şıkları İçin Özel Buton Varyantı
const option = defineStyle((props) => {
  // Bu varyant, doğrudan QuizActiveInterface içinde dinamik stillerle yönetilecek
  // Temel yapı ve geçişler burada tanımlanabilir.
  return {
    w: "100%",
    h: "auto",
    minH: { base: "50px", md: "55px" },
    py: { base: 3, md: 4 },
    px: { base: 4, md: 5 },
    justifyContent: "flex-start",
    textAlign: "left",
    whiteSpace: "normal",
    borderRadius: "lg", // Daha modern bir yuvarlaklık
    borderWidth: "1px", // Varsayılan kenarlık
    transitionProperty: "background-color, border-color, color, transform, box-shadow, opacity",
    transitionDuration: "var(--motion-duration-base)",
    transitionTimingFunction: "var(--motion-ease-apple)",
    // Varsayılan durum stilleri semantic token'lardan gelecek (optionDefaultBg vb.)
    // Farklı durumlar (selected, correct, incorrect, eliminated) için stiller
    // QuizActiveInterface içinde _selected, _correct vb. özel proplarla veya
    // doğrudan style prop'u ile yönetilecek.
    _hover: {
        // Bu hover efekti sadece cevaplanmamış ve elenmemiş şıklar için geçerli olmalı
        // QuizActiveInterface içinde dinamik olarak ayarlanacak.
        // Örnek: transform: "translateY(-2px) scale(1.01)", boxShadow: "md"
    },
    _disabled: { // Bu, cevap kontrol edildikten sonra diğer şıklar için kullanılabilir
        opacity: 0.7, // Hafif soluk
        cursor: "default", // Tıklanamaz değil, sadece pasif
        // _hover: {} // hover efektini kaldır
    },
    // Temel focus stili
    _focusVisible: {
        boxShadow: "var(--chakra-shadows-focusOutlineAccent)", // semantic token
        outline: "none",
    }
  };
});


// ====================================================================
// Faz 2 · Sinav (exam) ve SRS spesifik buton varyantlari.
// ====================================================================

// Generic factory: status-tonlu solid buton (exam.* / srs.* token namespace'ine bagli).
// Faz 1 fix · _focusVisible.boxShadow artik dogrudan semantic shadow token alir;
// Chakra `var(--chakra-shadows-focusRing-default)` CSS variable resolution'unu kendisi
// halleder, manuel `replace('.', '-')` cevirimine gerek kalmaz.
const makeStatusSolid = (statusPrefix, focusRingToken = 'focusRing.default') =>
  defineStyle(() => ({
    bg: `${statusPrefix}.bgSolid`,
    color: `${statusPrefix}.textOnSolid`,
    borderRadius: 'lg',
    fontWeight: 'semibold',
    transitionProperty: 'background-color, transform, box-shadow',
    transitionDuration: 'var(--motion-duration-fast)',
    transitionTimingFunction: 'var(--motion-ease-apple)',
    _hover: {
      bg: `${statusPrefix}.bgHover`,
      transform: 'translateY(-1px)',
      boxShadow: 'elevation.2',
    },
    _active: {
      transform: 'translateY(0)',
      boxShadow: 'elevation.1',
    },
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
    _focusVisible: {
      boxShadow: focusRingToken,
      outline: 'none',
    },
  }));

// Generic factory: status-tonlu subtle (yumusak) buton.
const makeStatusSubtle = (statusPrefix, focusRingToken = 'focusRing.default') =>
  defineStyle(() => ({
    bg: `${statusPrefix}.bg`,
    color: `${statusPrefix}.text`,
    borderWidth: '1px',
    borderColor: `${statusPrefix}.border`,
    borderRadius: 'lg',
    fontWeight: 'semibold',
    transitionProperty: 'background-color, border-color, transform',
    transitionDuration: 'var(--motion-duration-fast)',
    transitionTimingFunction: 'var(--motion-ease-apple)',
    _hover: {
      bg: `${statusPrefix}.bgSolid`,
      color: `${statusPrefix}.textOnSolid`,
      transform: 'translateY(-1px)',
    },
    _active: {
      transform: 'translateY(0)',
    },
    _disabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
      transform: 'none',
    },
    _focusVisible: {
      boxShadow: focusRingToken,
      outline: 'none',
    },
  }));

// Exam butonlari (sinav cevap ekrani, sonuc, isaretleme)
const examCorrect   = makeStatusSolid('exam.correct',   'focusRing.success');
const examIncorrect = makeStatusSolid('exam.incorrect', 'focusRing.error');
const examReview    = makeStatusSolid('exam.review',    'focusRing.warning');
const examPartial   = makeStatusSolid('exam.partial',   'focusRing.default');

// Exam subtle (sonuc gosterimi · outline)
const examCorrectSubtle   = makeStatusSubtle('exam.correct',   'focusRing.success');
const examIncorrectSubtle = makeStatusSubtle('exam.incorrect', 'focusRing.error');
const examReviewSubtle    = makeStatusSubtle('exam.review',    'focusRing.warning');

// SRS butonlari (flashcard ease · Anki tarzi)
const srsAgain = makeStatusSolid('srs.again', 'focusRing.error');
const srsHard  = makeStatusSolid('srs.hard',  'focusRing.warning');
const srsGood  = makeStatusSolid('srs.good',  'focusRing.success');
const srsEasy  = makeStatusSolid('srs.easy',  'focusRing.default');

// Danger / success subtle (mevcut solid'lere alternatif dusuk-emphasis)
const dangerSubtle = defineStyle(() => ({
  bg: 'rose.50',
  color: 'rose.700',
  borderWidth: '1px',
  borderColor: 'rose.200',
  borderRadius: 'lg',
  fontWeight: 'semibold',
  transitionProperty: 'common',
  transitionDuration: 'var(--motion-duration-fast)',
  transitionTimingFunction: 'var(--motion-ease-apple)',
  _hover: { bg: 'rose.100', borderColor: 'rose.300' },
  _active: { bg: 'rose.200' },
  _disabled: { opacity: 0.5, cursor: 'not-allowed' },
  _focusVisible: { boxShadow: 'focusRing.error', outline: 'none' },
  _dark: {
    bg: 'rose.900',
    color: 'rose.200',
    borderColor: 'rose.700',
    _hover: { bg: 'rose.800', borderColor: 'rose.600' },
    _active: { bg: 'rose.700' },
  },
}));

const successSubtle = defineStyle(() => ({
  bg: 'emerald.50',
  color: 'emerald.700',
  borderWidth: '1px',
  borderColor: 'emerald.200',
  borderRadius: 'lg',
  fontWeight: 'semibold',
  transitionProperty: 'common',
  transitionDuration: 'var(--motion-duration-fast)',
  transitionTimingFunction: 'var(--motion-ease-apple)',
  _hover: { bg: 'emerald.100', borderColor: 'emerald.300' },
  _active: { bg: 'emerald.200' },
  _disabled: { opacity: 0.5, cursor: 'not-allowed' },
  _focusVisible: { boxShadow: 'focusRing.success', outline: 'none' },
  _dark: {
    bg: 'emerald.900',
    color: 'emerald.200',
    borderColor: 'emerald.700',
    _hover: { bg: 'emerald.800', borderColor: 'emerald.600' },
    _active: { bg: 'emerald.700' },
  },
}));

// Premium (amber gradient · odul, abone yukseltme)
const premium = defineStyle(() => ({
  bgGradient: 'linear(135deg, amber.400, amber.600)',
  color: 'white',
  borderRadius: 'lg',
  fontWeight: 'bold',
  transitionProperty: 'common',
  transitionDuration: 'var(--motion-duration-base)',
  transitionTimingFunction: 'var(--motion-ease-apple)',
  boxShadow: 'elevation.2',
  _hover: {
    bgGradient: 'linear(135deg, amber.500, amber.700)',
    transform: 'translateY(-2px)',
    boxShadow: 'elevation.3',
  },
  _active: {
    transform: 'translateY(-1px)',
    boxShadow: 'elevation.1',
  },
  _disabled: { opacity: 0.5, cursor: 'not-allowed', transform: 'none', boxShadow: 'none' },
  _focusVisible: { boxShadow: 'focusRing.warning', outline: 'none' },
}));

// Tutarli buton boyutlari · min tap target (44px) iOS HIG uyumlu.
// xs: kompakt inline butonlar (chip + action)
// sm: toolbar, secondary action
// md: varsayilan form butonu (40px height)
// lg: primary CTA (48px height)
// xl: hero CTA (56px height)
const buttonSizes = {
  xs: defineStyle({ h: 7,  minW: 7,  fontSize: 'xs', px: 2.5, borderRadius: 'md' }),  // 28px
  sm: defineStyle({ h: 8,  minW: 8,  fontSize: 'sm', px: 3,   borderRadius: 'md' }),  // 32px
  md: defineStyle({ h: 10, minW: 10, fontSize: 'sm', px: 4,   borderRadius: 'lg' }),  // 40px
  lg: defineStyle({ h: 12, minW: 12, fontSize: 'md', px: 6,   borderRadius: 'lg' }),  // 48px
  xl: defineStyle({ h: 14, minW: 14, fontSize: 'md', px: 8,   borderRadius: 'xl' }),  // 56px
};

export const buttonTheme = defineStyleConfig({
  baseStyle: {
    fontWeight: "semibold",
    borderRadius: "lg",
    transitionProperty: "common",
    transitionDuration: "var(--motion-duration-base)",
    transitionTimingFunction: "var(--motion-ease-apple)",
    // iOS Safari tap delay + tap-highlight fix
    touchAction: "manipulation",
    WebkitTapHighlightColor: "transparent",
    _focusVisible: {
      boxShadow: "var(--chakra-shadows-focusOutlineAccent)",
      outline: "none",
    },
    // Faz 2 a11y · disabled durumda hover/active tetiklenmesini engelle.
    // Variant'larin kendi _disabled style'lari opacity/bg/cursor'i ezebilir;
    // pointerEvents: 'none' baseStyle'da tek noktadan uygulanir.
    _disabled: {
      pointerEvents: "none",
    },
  },
  sizes: buttonSizes,
  variants: {
    aiSolid,
    aiOutline,
    option,
    glass,
    authPrimary,
    authOutline,
    // Exam status (doğru/yanlış/review/partial)
    examCorrect,
    examIncorrect,
    examReview,
    examPartial,
    examCorrectSubtle,
    examIncorrectSubtle,
    examReviewSubtle,
    // SRS ease butonlari
    srsAgain,
    srsHard,
    srsGood,
    srsEasy,
    // Subtle/Premium
    dangerSubtle,
    successSubtle,
    premium,
    // Chakra defaults'u AI temamiza yonlendir
    solid: aiSolid,
    outline: aiOutline,
    ghost: defineStyle((props) => ({
      color: props.colorMode === 'dark' ? "whiteAlpha.700" : "gray.600",
      _hover: {
        bg: props.colorMode === 'dark' ? "whiteAlpha.100" : "gray.100",
        color: props.colorMode === 'dark' ? "whiteAlpha.900" : "gray.800",
      },
      _active: {
        bg: props.colorMode === 'dark' ? "whiteAlpha.200" : "gray.200",
      },
    })),
    link: defineStyle(() => ({
      color: "accent",
      textDecoration: "none",
      _hover: {
        color: "accentHover",
        textDecoration: "underline",
      },
    })),
  },
  defaultProps: {
    variant: "aiSolid",
    colorScheme: "brand",
    size: "md",
  },
});

export default buttonTheme; // index.js'de Button: buttonTheme olarak kullanılacak
